import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  output,
  inject,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Playlist, PlaylistListItem, Action } from '@bossanova/models';
import { ImageUrlPipe } from '../pipes/image-url.pipe';
import { YoutubeSrcsetPipe } from '../pipes/youtube-srcset.pipe';
import { PureAbility } from '@casl/ability';
import { ButtonComponent } from '../button/button.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'bn-playlist-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    NgOptimizedImage,
    ImageUrlPipe,
    YoutubeSrcsetPipe,
    ButtonComponent,
  ],
  templateUrl: './playlist-card.component.html',
  styleUrl: './playlist-card.component.scss',
})
export class PlaylistCardComponent {
  private dialog = inject(MatDialog);
  private ability = inject(PureAbility);

  playlist = input.required<Playlist | PlaylistListItem>();
  priority = input<boolean>(false);
  url = input<string | any[]>();
  displayModifyControls = input<boolean>(false);

  deleteConfirmed = output<Playlist | PlaylistListItem>();

  deletePlaylist(playlist: Playlist | PlaylistListItem): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete Playlist',
          message: `Are you sure you want to delete "${playlist.title}"?`,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteConfirmed.emit(playlist);
        }
      });
  }

  thumbnail = computed(() => {
    const p = this.playlist();
    return (p as PlaylistListItem).thumbnailUrl || p.imagePath;
  });

  authorAvatar = computed(() => {
    const p = this.playlist();
    return (p as PlaylistListItem).owner?.avatarPath;
  });

  authorName = computed(() => {
    const p = this.playlist();
    return (p as PlaylistListItem).owner?.username;
  });

  cardUrl = computed(() => {
    const customUrl = this.url();
    const p = this.playlist();
    if (p.shortId && p.slug) {
      return customUrl || ['/playlist', p.shortId, p.slug];
    }
    return customUrl || ['/playlist', p.id];
  });

  canUpdate = computed(() => this.ability.can(Action.Update, this.playlist()));
  canDelete = computed(() => this.ability.can(Action.Delete, this.playlist()));
}
